import { updateValuesWrapper } from "@repo/dynamicEditor/src/helpers/updatePropertiesHooks";
import { PushProcessingNotification } from "@repo/helpers";
import { useCallback, useMemo } from "react";

import { useTrainingApi, useTrainingQueries } from "../../../api/trainingApi";
import { EditableItemDynamicTab } from "../../../components/dynamicEditor/dynamicTab/editableItem";
import { ITemplateData } from "../../../components/dynamicEditor/templateDataHook";
import { useRepContext, useTrainerCertificationContext } from "../../../contexts/editableItemContext";
import { ITrainerCertification } from "../../../models/MVCStateSpecificTraining";
import { tabs } from "../../../string";

interface IState extends ITemplateData {
	trainerCertification: ITrainerCertification;
}

function useStateSpecificTrainingLists() {
	const { useStatesQuery } = useTrainingQueries();
	const { data } = useStatesQuery();

	const activeLists = useMemo(() => {
		return {
			states: data?.map((d) => ({ id: d.id, value: d.name ?? d.value })),
		};
	}, [data]);

	return [activeLists];
}

function TrainerCertificationEditorDynamic() {
	const { repId, rep, updateItem: updateRep } = useRepContext();
	const isTrainer = rep?.isTrainer;
	const context = useTrainerCertificationContext();
	const [trainingLists] = useStateSpecificTrainingLists();
	const { saveTrainerCertification, getTrainerCertification } = useTrainingApi();

	const trainerCertification = useMemo(() => ({ ...context.item, isTrainer }), [context.item, isTrainer]);
	const templateData = useMemo(
		() => ({
			trainerCertification,
			trainingLists,
		}),
		[trainerCertification, rep, trainingLists],
	);

	const { useTrainerCertificationsQuery } = useTrainingQueries();
	const { clearQuery } = useTrainerCertificationsQuery();

	const save = useCallback(async () => {
		const notify = PushProcessingNotification("Saving Training...");
		try {
			const id = await saveTrainerCertification(repId, trainerCertification);
			notify.processing("Loading...");
			const newTraining = await getTrainerCertification(id);
			clearQuery();
			notify.success("Saved!");
			return newTraining;
		} catch (ex) {
			notify.error("Failed to save");
			throw ex;
		}
	}, [repId, trainerCertification]);

	const updateValues = useCallback(updateValuesWrapper(applyBusinessLogic), [applyBusinessLogic]);

	async function applyBusinessLogic(id: string, value: any, newState: IState) {
		//Custom logic
		switch (id) {
			case "trainerCertification.isTrainer":
				updateRep({ isTrainer: value });
				break;
			default:
				break;
		}

		return newState as IState;
	}

	return (
		<EditableItemDynamicTab
			itemContext={context}
			pageName={tabs.trainerCert}
			otherTemplateData={templateData}
			itemProp="trainerCertification"
			updateValues={updateValues}
			save={save}
			loaded
		/>
	);
}

export default TrainerCertificationEditorDynamic;
